import React, { useState, useMemo } from 'react';
import { SearchAutocomplete } from './SearchAutocomplete';
import { getSafeProductImages, getSafeProductFirstImage } from '../lib/productImages';
import {
  Search,
  Sparkles,
  ShieldCheck,
  Download,
  Layers,
  Star,
  CheckCircle2,
  TrendingUp,
  SlidersHorizontal,
  ArrowRight,
  FileCheck2,
  Award,
  Zap,
  Lock,
  Headphones,
  Check,
  FileText,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Tag
} from 'lucide-react';
import { DocumentCategory, Product, LiveSiteStatistics, CMSContent, HeroTrustBadgeItem } from '../types';

interface Hero3DProps {
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  selectedCategory: DocumentCategory | 'All';
  setSelectedCategory: (cat: DocumentCategory | 'All') => void;
  onOpenAI: () => void;
  darkMode: boolean;
  products?: Product[];
  onSelectProduct?: (product: Product) => void;
  onPerformSearch?: (query: string) => void;
  stats?: LiveSiteStatistics;
  cms?: CMSContent;
}

export const Hero3D: React.FC<Hero3DProps> = ({
  searchQuery,
  setSearchQuery,
  selectedCategory,
  setSelectedCategory,
  onOpenAI,
  darkMode,
  products = [],
  onSelectProduct = (_product?: Product) => {},
  onPerformSearch = (_query?: string) => {},
  stats,
  cms,
}) => {
  // Active category pills for quick filtering
  const popularPills: DocumentCategory[] = [
    'Passport',
    'ID Card',
    'Driving License',
    'Certificate',
    'Bank Statement',
    'Utility Bill',
    'Resume'
  ];

  // Dynamic Calculated Statistics
  const totalProducts = stats?.totalPublishedProducts ?? products.filter(p => !p.status || p.status === 'published' || p.status === 'active').length;
  const totalDownloads = stats?.totalDownloads ?? 0;
  const avgRating = stats?.averageRating ?? 4.92;
  const totalCategories = stats?.totalCategories ?? 10;
  const totalCountries = stats?.totalCountries ?? 25;

  // Program Master On/Off Switch & Preview Mode
  const heroFloatingCardsEnabled = cms?.heroFloatingCardsEnabled !== false;
  const heroPreviewMode = cms?.heroPreviewMode || 'custom_images';

  // Mode 1: Custom Image Cards
  const customCardsList = useMemo(() => {
    if (cms?.heroCustomCards && cms.heroCustomCards.length > 0) {
      return cms.heroCustomCards.filter(c => c && c.id && !c.id.includes('demo') && !c.id.includes('fallback'));
    }
    return [];
  }, [cms?.heroCustomCards]);

  // Mode 2: Catalog Products
  const catalogProductsList = useMemo(() => {
    if (cms?.heroFeaturedProductIds && cms.heroFeaturedProductIds.length > 0) {
      const selected = products.filter(p => cms.heroFeaturedProductIds?.includes(p.id));
      if (selected.length > 0) return selected;
    }
    const featured = products.filter(p => p.isPopular || p.tags?.includes('featured') || (p as any).isFeatured);
    return featured.length > 0 ? featured : products.slice(0, 6);
  }, [products, cms?.heroFeaturedProductIds]);

  const totalItemsCount = heroPreviewMode === 'custom_images' ? customCardsList.length : catalogProductsList.length;

  const [activePreviewIndex, setActivePreviewIndex] = useState<number>(0);
  const [activeSubImageIndex, setActiveSubImageIndex] = useState<number>(0);

  // Auto-Slide Interval Timer (Automatic Image / Product Changes)
  React.useEffect(() => {
    if (!heroFloatingCardsEnabled || totalItemsCount <= 1) return;
    const intervalTime = cms?.heroAutoSlideInterval || 4000;
    const timer = setInterval(() => {
      setActiveSubImageIndex(0);
      setActivePreviewIndex((prev) => (prev + 1) % totalItemsCount);
    }, intervalTime);
    return () => clearInterval(timer);
  }, [heroFloatingCardsEnabled, totalItemsCount, cms?.heroAutoSlideInterval, heroPreviewMode]);

  // If Hero is set to hidden in CMS, return null
  if (cms?.heroVisibility === false) {
    return null;
  }

  // Active Items for Modes
  const activeCustomCard = customCardsList[activePreviewIndex % customCardsList.length];
  const activeProduct = catalogProductsList[activePreviewIndex % catalogProductsList.length] || products[0];

  const activeProductImages = getSafeProductImages(activeProduct);

  const currentDisplayImage = activeProductImages[activeSubImageIndex % activeProductImages.length] || (activeProduct as any)?.previewUrl;

  const handleNextPreview = () => {
    if (totalItemsCount === 0) return;
    setActiveSubImageIndex(0);
    setActivePreviewIndex((prev) => (prev + 1) % totalItemsCount);
  };

  const handlePrevPreview = () => {
    if (totalItemsCount === 0) return;
    setActiveSubImageIndex(0);
    setActivePreviewIndex((prev) => (prev - 1 + totalItemsCount) % totalItemsCount);
  };

  // Helper to resolve icon components for Trust Badges
  const renderTrustBadgeIcon = (iconName?: string) => {
    switch (iconName?.toLowerCase()) {
      case 'download':
      case 'instant download':
        return <Download className="w-4 h-4 text-cyan-400" />;
      case 'layers':
      case '100% editable files':
        return <Layers className="w-4 h-4 text-purple-400" />;
      case 'shieldcheck':
      case 'secure checkout':
        return <ShieldCheck className="w-4 h-4 text-emerald-400" />;
      case 'award':
      case 'commercial license':
        return <Award className="w-4 h-4 text-amber-400" />;
      case 'sparkles':
      case 'premium quality':
        return <Sparkles className="w-4 h-4 text-blue-400" />;
      case 'checkcircle2':
      case 'verified marketplace':
        return <CheckCircle2 className="w-4 h-4 text-teal-400" />;
      case 'headphones':
      case '24/7 support':
        return <Headphones className="w-4 h-4 text-indigo-400" />;
      case 'zap':
        return <Zap className="w-4 h-4 text-yellow-400" />;
      case 'lock':
        return <Lock className="w-4 h-4 text-emerald-400" />;
      default:
        return <CheckCircle2 className="w-4 h-4 text-cyan-400" />;
    }
  };

  // CMS Content values with strict defaults
  const heroBadgeText = cms?.heroBadge || 'WORLD-CLASS DIGITAL TEMPLATE MARKETPLACE';
  const heroTitleText = cms?.heroTitle || 'Premium Editable PSD, Vector & Document Templates';
  const heroSubtitleText =
    cms?.heroSubtitle ||
    'Download professionally designed editable PSD, AI, Vector and CDR templates including Passports, ID Cards, Driving Licenses, Certificates, Utility Bills, Bank Statements, Resume Templates, Health ID and many more. Instant download, commercial quality, fully customizable.';
  
  const primaryCtaText = cms?.heroPrimaryCtaText || 'Explore Marketplace';
  const secondaryCtaText = cms?.heroSecondaryCtaText || 'Browse Categories';

  const trustBadgesList: HeroTrustBadgeItem[] = cms?.heroTrustBadges && cms.heroTrustBadges.length > 0
    ? cms.heroTrustBadges.filter(b => b.enabled !== false)
    : [
        { id: 'b1', label: 'Instant Download', icon: 'Download', enabled: true },
        { id: 'b2', label: '100% Editable Files', icon: 'Layers', enabled: true },
        { id: 'b3', label: 'Secure Checkout', icon: 'ShieldCheck', enabled: true },
        { id: 'b4', label: 'Commercial License', icon: 'Award', enabled: true },
        { id: 'b5', label: 'Premium Quality', icon: 'Sparkles', enabled: true },
        { id: 'b6', label: 'Verified Marketplace', icon: 'CheckCircle2', enabled: true },
        { id: 'b7', label: '24/7 Support', icon: 'Headphones', enabled: true },
      ];

  const handlePrimaryClick = () => {
    const el = document.getElementById('products') || document.getElementById('marketplace-grid');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      setSelectedCategory('All');
    }
  };

  const handleSecondaryClick = () => {
    const el = document.getElementById('categories') || document.getElementById('categories-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative overflow-hidden pt-12 pb-20 lg:pt-16 lg:pb-28">
      
      {/* Background Media / Image Overlay if configured in Admin */}
      {cms?.heroBgImageUrl && (
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          <img
            src={cms.heroBgImageUrl}
            alt="Hero Background Overlay"
            className="w-full h-full object-cover"
            style={{ opacity: cms.heroOverlayOpacity ?? 0.2 }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/90 to-slate-950"></div>
        </div>
      )}

      {/* Ambient Glass Glow Orbs */}
      {(cms?.heroAnimationEnabled !== false) && (
        <>
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[650px] h-[380px] bg-gradient-to-tr from-blue-600/30 via-purple-600/20 to-cyan-500/30 rounded-full blur-[120px] pointer-events-none animate-pulse-glow" />
          <div className="absolute top-10 -left-20 w-80 h-80 bg-blue-500/20 rounded-full blur-[90px] pointer-events-none" />
          <div className="absolute top-20 -right-20 w-96 h-96 bg-purple-500/20 rounded-full blur-[100px] pointer-events-none" />
        </>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          
          {/* Left Hero Content */}
          <div className="lg:col-span-7 text-center lg:text-left space-y-4 sm:space-y-6 min-w-0 flex flex-col items-center lg:items-start">
            
            {/* Top Badge */}
            <div className="inline-flex items-center gap-2 pl-3 pr-4 py-1.5 sm:pl-3 sm:pr-4 sm:py-2 rounded-full glass-card border border-cyan-500/30 shadow-lg shadow-cyan-500/10 max-w-full">
              <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cyan-400 animate-spin shrink-0" style={{ animationDuration: '8s' }} />
              <span className="text-[11px] sm:text-xs font-bold tracking-wide uppercase text-gradient truncate">
                {heroBadgeText}
              </span>
            </div>

            {/* Main SEO H1 Title */}
            <h1 className="text-2xl sm:text-4xl lg:text-6xl font-bold sm:font-black tracking-tight leading-snug sm:leading-[1.15] break-words text-center lg:text-left">
              {heroTitleText.includes('PSD') || heroTitleText.includes('Vector') ? (
                <>
                  {heroTitleText.split(',')[0]}
                  {heroTitleText.split(',').length > 1 && (
                    <span className="block text-gradient mt-0.5 sm:mt-1">
                      {heroTitleText.split(',').slice(1).join(',').trim()}
                    </span>
                  )}
                </>
              ) : (
                heroTitleText
              )}
            </h1>

            {/* Subtitle / Description */}
            <p className={`text-xs sm:text-base lg:text-lg max-w-2xl font-normal leading-relaxed break-words mx-auto lg:mx-0 text-center lg:text-left ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              {heroSubtitleText}
            </p>



            {/* Live Search Bar */}
            <div className="pt-2 w-full">
              <div className="relative group max-w-2xl w-full mx-auto lg:mx-0">
                <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-400 opacity-60 blur-md group-hover:opacity-100 transition duration-500"></div>
                <div className="relative z-10">
                  <SearchAutocomplete
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    onSelectProduct={onSelectProduct}
                    onPerformSearch={onPerformSearch}
                    products={products}
                    darkMode={darkMode}
                    size="lg"
                    placeholder={`Search ${totalProducts} templates (e.g. Passport PSD, Driving License, Utility Bill, Bank Statement)...`}
                  />
                </div>
              </div>
            </div>

            {/* Quick Category Pills */}
            <div className="flex items-center justify-center lg:justify-start gap-2 pt-1 overflow-x-auto max-w-full pb-2 scrollbar-none w-full">
              <span className="text-xs font-semibold text-slate-400 flex-shrink-0">
                Trending:
              </span>
              {popularPills.map((pill) => (
                <button
                  key={pill}
                  onClick={() => setSelectedCategory(pill)}
                  className={`px-3 py-1 rounded-xl text-xs font-bold transition-all duration-200 border flex-shrink-0 ${
                    selectedCategory === pill
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white border-cyan-400 shadow-md shadow-cyan-500/20'
                      : darkMode
                      ? 'bg-slate-800/40 border-slate-700/60 text-slate-300 hover:bg-slate-700/50 hover:text-white'
                      : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {pill}
                </button>
              ))}
            </div>

          </div>

          {/* Right Hero Visual Section: Floating Dynamic Marketplace Featured Preview Cards */}
          {heroFloatingCardsEnabled && (
            <div className="lg:col-span-5 relative w-full max-w-full overflow-hidden">
              {heroPreviewMode === 'custom_images' ? (
                /* MODE 1: CUSTOM UPLOADED IMAGES MODE */
                <div className="relative w-full rounded-2xl sm:rounded-3xl glass-card p-3 sm:p-5 shadow-2xl border border-white/20 hover:border-cyan-500/40 transition-all duration-500 group max-w-full overflow-hidden">
                  {/* Header Badge & Controls */}
                  <div className="flex items-center justify-between border-b border-white/10 pb-2 mb-2.5 gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-ping shrink-0"></span>
                      <span className="text-[11px] sm:text-xs font-bold text-cyan-300 uppercase tracking-wider truncate">
                        {activeCustomCard?.badgeText || cms?.heroCustomBadgeText || 'AVAILABLE NEW PRODUCT'}
                      </span>
                    </div>

                    {customCardsList.length > 1 && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          type="button"
                          onClick={handlePrevPreview}
                          className="p-1 rounded-lg sm:rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors"
                          title="Previous Image"
                        >
                          <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                        <span className="text-[10px] sm:text-[11px] font-mono font-bold text-slate-400 px-1">
                          {(activePreviewIndex % customCardsList.length) + 1}/{customCardsList.length}
                        </span>
                        <button
                          type="button"
                          onClick={handleNextPreview}
                          className="p-1 rounded-lg sm:rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors"
                          title="Next Image"
                        >
                          <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Custom Preview Image Showcase */}
                  <div className="relative rounded-xl sm:rounded-2xl overflow-hidden border border-white/10 aspect-[16/10] bg-slate-950 select-none flex items-center justify-center max-w-full">
                    <img
                      src={activeCustomCard?.imageUrl || getSafeProductFirstImage(null)}
                      alt={activeCustomCard?.title || 'Custom Preview'}
                      title={activeCustomCard?.title || 'Custom Preview'}
                      loading="eager"
                      
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-contain sm:object-cover md:group-hover:scale-105 transition-all duration-700 bg-slate-950"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/20 to-transparent pointer-events-none"></div>

                    {/* Software Formats Badge */}
                    <div className="absolute top-2 left-2 flex flex-wrap gap-1 z-10">
                      {(activeCustomCard?.formats || ['PSD', 'AI']).map((fmt: string) => (
                        <span
                          key={fmt}
                          className="px-1.5 py-0.5 rounded bg-slate-950 text-cyan-300 font-mono font-bold text-[9px] sm:text-[10px] border border-cyan-500/30 shadow-md"
                        >
                          .{fmt}
                        </span>
                      ))}
                    </div>

                    {/* Bottom Overlay Title */}
                    <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2 z-10">
                      <div className="min-w-0 flex-1">
                        <span className="px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 text-[9px] sm:text-[10px] font-bold border border-purple-500/30">
                          {activeCustomCard?.category || 'Custom Showcase'}
                        </span>
                        <h3 className="text-xs sm:text-sm font-bold text-white truncate mt-1 drop-shadow-md">
                          {activeCustomCard?.title || 'LUXEMBOURG VICTOR PASSPORT'}
                        </h3>
                      </div>
                      <span className="px-1.5 py-0.5 rounded-lg bg-slate-900/90 text-slate-300 font-bold text-[9px] sm:text-[10px] border border-slate-700 shrink-0">
                        Preview
                      </span>
                    </div>
                  </div>

                  {/* Floating Micro Highlights */}
                  <div className="mt-2.5 grid grid-cols-2 gap-1.5 sm:gap-2 pt-2 border-t border-white/10">
                    <div className="flex items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 rounded-xl bg-slate-900/60 border border-slate-800 min-w-0">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0">
                        <FileCheck2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9.5px] sm:text-[11px] font-bold text-white truncate leading-tight">100% Vector</p>
                        <p className="text-[8.5px] sm:text-[10px] text-slate-400 truncate leading-tight">Graphic Layout</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 rounded-xl bg-slate-900/60 border border-slate-800 min-w-0">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400 shrink-0">
                        <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9.5px] sm:text-[11px] font-bold text-white truncate leading-tight">High Quality</p>
                        <p className="text-[8.5px] sm:text-[10px] text-slate-400 truncate leading-tight">RH Editography</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* MODE 2: CATALOG PRODUCTS MODE */
                activeProduct && (
                  <div className="relative w-full rounded-2xl sm:rounded-3xl glass-card p-3 sm:p-5 shadow-2xl border border-white/20 hover:border-cyan-500/40 transition-all duration-500 group max-w-full overflow-hidden">
                    {/* Header Badge & Controls */}
                    <div className="flex items-center justify-between border-b border-white/10 pb-2.5 mb-3 gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping shrink-0"></span>
                        <span className="text-[11px] sm:text-xs font-bold text-cyan-300 uppercase tracking-wider truncate">
                          Featured Product
                        </span>
                      </div>

                      {catalogProductsList.length > 1 && (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            type="button"
                            onClick={handlePrevPreview}
                            className="p-1 rounded-lg sm:rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors"
                            title="Previous Product"
                          >
                            <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          <span className="text-[10px] sm:text-[11px] font-mono font-bold text-slate-400 px-1">
                            {(activePreviewIndex % catalogProductsList.length) + 1}/{catalogProductsList.length}
                          </span>
                          <button
                            type="button"
                            onClick={handleNextPreview}
                            className="p-1 rounded-lg sm:rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors"
                            title="Next Product"
                          >
                            <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Main Product Preview Image */}
                    <div
                      onClick={() => onSelectProduct(activeProduct)}
                      className="relative rounded-xl sm:rounded-2xl overflow-hidden border border-white/10 aspect-[16/10] bg-slate-900 cursor-pointer max-w-full"
                    >
                      <img
                        src={currentDisplayImage || getSafeProductFirstImage(activeProduct)}
                        alt={activeProduct.title}
                        title={activeProduct.title}
                        loading="eager"
                        
                        referrerPolicy="no-referrer"
                        className="w-full h-full object-contain sm:object-cover md:group-hover:scale-105 transition-all duration-700 bg-slate-950"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/20 to-transparent pointer-events-none"></div>

                      {/* Multi-Image Dots in Hero Featured Card */}
                      {activeProductImages.length > 1 && (
                        <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-1 z-20 bg-slate-950/95 px-2 py-0.5 rounded-full border border-white/20">
                          {activeProductImages.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveSubImageIndex(idx);
                              }}
                              className={`h-1 rounded-full transition-all duration-300 ${
                                activeSubImageIndex === idx ? 'w-3 bg-cyan-400' : 'w-1 bg-white/40 hover:bg-white'
                              }`}
                              title={`Slide ${idx + 1}`}
                            />
                          ))}
                        </div>
                      )}

                      {/* Top Left Software Formats */}
                      <div className="absolute top-2 left-2 flex flex-wrap gap-1 z-10">
                        {activeProduct.formats?.map((fmt) => (
                          <span
                            key={fmt}
                            className="px-1.5 py-0.5 rounded bg-slate-950 text-cyan-300 font-mono font-bold text-[9px] sm:text-[10px] border border-cyan-500/30 shadow-md"
                          >
                            .{fmt}
                          </span>
                        ))}
                      </div>

                      {/* Top Right Price Tag */}
                      <div className="absolute top-2 right-2 px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-lg sm:rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-black text-[10px] sm:text-xs shadow-lg border border-white/20 z-10">
                        ${activeProduct.price.toFixed(2)}
                      </div>

                      {/* Bottom Overlay Title & Category */}
                      <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2 z-10">
                        <div className="min-w-0 flex-1">
                          <span className="px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 text-[9px] sm:text-[10px] font-bold border border-blue-500/30">
                            {activeProduct.category}
                          </span>
                          <h3 className="text-xs sm:text-sm font-bold text-white truncate mt-1 drop-shadow-md">
                            {activeProduct.title}
                          </h3>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectProduct(activeProduct);
                          }}
                          className="px-2.5 py-1 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/40 text-cyan-300 font-bold text-[10px] sm:text-xs border border-cyan-400/40 transition-colors flex items-center gap-1 shrink-0"
                        >
                          <span>Inspect</span>
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>

                    {/* Floating Micro Highlights */}
                    <div className="mt-2.5 grid grid-cols-2 gap-2 pt-2 border-t border-white/10">
                      <div className="flex items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 rounded-xl bg-slate-900/60 border border-slate-800 min-w-0">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0">
                          <FileCheck2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] sm:text-[11px] font-bold text-white truncate">100% Editable</p>
                          <p className="text-[9px] sm:text-[10px] text-slate-400 truncate">Smart Layer PSD</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 rounded-xl bg-slate-900/60 border border-slate-800 min-w-0">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400 shrink-0">
                          <Download className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] sm:text-[11px] font-bold text-white truncate">Instant Download</p>
                          <p className="text-[9px] sm:text-[10px] text-slate-400 truncate">Commercial</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          )}

        </div>

      </div>
    </section>
  );
};
